import React from 'react';
import { Filter, X } from 'lucide-react';
import Select from 'react-select';
import { AssetType, statusConfig, assetTypeConfig } from './types';
import FilterChip from './FilterChip';

interface FilterSectionProps {
  statusFilters: Set<string>;
  toggleStatusFilter: (status: string) => void;
  assetTypeFilters: Set<AssetType>;
  toggleAssetTypeFilter: (assetType: AssetType) => void;
  userFilter: string;
  setUserFilter: (filter: string) => void;
  selectedAssetType: AssetType | null;
  setSelectedAssetType: (assetType: AssetType | null) => void;
  selectedBerth: string | null;
  setSelectedBerth: (berth: string | null) => void;
  resetFilters: () => void;
  getStatusCount: (status: string, useFiltered?: boolean) => number;
  berthOptions: Array<{ value: string; label: string }>;
  addonOptions: Array<{ value: string; label: string }>;
  getAddonName: (addonId: string | null) => string;
  resourceOptions?: Array<{ value: string; label: string }>;
  getResourceName?: (resourceId: string | null) => string;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  statusFilters,
  toggleStatusFilter,
  assetTypeFilters,
  userFilter,
  setUserFilter,
  selectedAssetType,
  setSelectedAssetType,
  selectedBerth,
  setSelectedBerth,
  resetFilters,
  getStatusCount,
  berthOptions,
  addonOptions,
  getAddonName,
  resourceOptions = [],
  getResourceName = () => 'Resource',
}) => {
  const handleAssetTypeChange = (value: string | null) => {
    if (!value) {
      setSelectedAssetType(null);
      setSelectedBerth(null);
      return;
    }

    setSelectedAssetType(value as AssetType);
    setSelectedBerth(null); // Reset berth selection when asset type changes
  };

  const visibleStatusEntries = React.useMemo(
    () =>
      Object.entries(statusConfig).filter(([status]) => status !== 'cancelled'),
    []
  );
  const totalStatuses = visibleStatusEntries.length;

  const assetTypeOptions = React.useMemo(() => {
    const filteredAssetTypes = assetTypeFilters?.size
      ? Object.entries(assetTypeConfig).filter(([assetType]) =>
          assetTypeFilters.has(assetType as AssetType)
        )
      : Object.entries(assetTypeConfig);

    return filteredAssetTypes.map(([assetType, config]) => ({
      value: assetType as AssetType,
      label: config.label,
      color: config.color,
    }));
  }, [assetTypeFilters]);

  const isAssetTypeLocked = assetTypeOptions.length <= 1;

  return (
    <div className="mb-8">
      <div className="rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 contrast:border-gray-700 overflow-hidden">
        <div className="bg-blue-100 dark:bg-blue-950 contrast:bg-blue-950 px-6 py-4 border-b border-gray-100 dark:border-gray-800 contrast:border-gray-800">
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-300 dark:bg-blue-700 contrast:bg-blue-700 rounded-lg">
                <Filter className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 contrast:text-white">
                  Filters & Search
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-30 contrast:text-gray-500">
                  Customize your calendar view
                </p>
              </div>
            </div>
            <button
              onClick={resetFilters}
              className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-200 contrast:text-gray-200 bg-white dark:bg-gray-800 contrast:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 contrast:hover:bg-gray-700 rounded-lg flex items-center gap-2 border border-gray-200 dark:border-gray-700 contrast:border-gray-700 shadow-sm"
              aria-label="Reset all filters"
            >
              <X className="w-4 h-4 contrast:text-primary" />
              Reset All
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Status Filter Chips */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 contrast:text-white/70 mb-3">
              Status Filters
            </h3>
            <div className="flex flex-wrap gap-2">
              {visibleStatusEntries.map(([status, config]) => (
                <FilterChip
                  key={status}
                  active={statusFilters.has(status)}
                  label={config.label}
                  mainColor={config.color}
                  lightColor={config.lightColor}
                  textColor={config.textColor}
                  borderColor={config.borderColor}
                  count={getStatusCount(status, true)}
                  onClick={() => toggleStatusFilter(status)}
                />
              ))}
            </div>
          </div>

          {/* Advanced Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Asset Type Selector */}
            <div>
              <span className="block text-sm font-medium text-gray-700  contrast:text-white/70 mb-2">
                Asset Type
              </span>
              <Select
                options={assetTypeOptions}
                value={
                  selectedAssetType
                    ? {
                        value: selectedAssetType,
                        label: assetTypeConfig[selectedAssetType].label,
                        color: assetTypeConfig[selectedAssetType].color,
                      }
                    : null
                }
                onChange={(selected) => {
                  handleAssetTypeChange(selected ? selected.value : null);
                }}
                placeholder="All asset types"
                className="react-select-container"
                classNamePrefix="react-select"
                isClearable={!isAssetTypeLocked}
                isDisabled={isAssetTypeLocked}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                aria-label="Asset Type"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderRadius: '0.75rem',
                    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                    minHeight: '2.75rem',
                    fontSize: '0.875rem',
                    boxShadow: state.isFocused
                      ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
                      : 'none',
                    '&:hover': { borderColor: '#3b82f6' },
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                    borderRadius: '0.75rem',
                    overflow: 'hidden',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>

            {/* Specific Asset Selector — hidden for service (no per-service sub-filter) */}
            {selectedAssetType && selectedAssetType !== 'service' && (
              <div>
                <label
                  htmlFor="specific-asset"
                  className="block text-sm font-medium text-gray-700  contrast:text-white/70 mb-2"
                >
                  {selectedAssetType === 'berth'
                    ? 'Select Berth'
                    : selectedAssetType === 'resource'
                      ? 'Select Resource'
                      : 'Select Add-on'}
                </label>
                <Select
                  id="specific-asset"
                  options={
                    selectedAssetType === 'berth'
                      ? berthOptions
                      : selectedAssetType === 'resource'
                        ? resourceOptions
                        : addonOptions
                  }
                  value={
                    selectedBerth
                      ? {
                          value: selectedBerth,
                          label:
                            selectedAssetType === 'berth'
                              ? `Berth ${selectedBerth}`
                              : selectedAssetType === 'resource'
                                ? getResourceName(selectedBerth)
                                : getAddonName(selectedBerth),
                        }
                      : null
                  }
                  onChange={(selected) => {
                    setSelectedBerth(selected ? selected.value : null);
                  }}
                  placeholder={`Select ${selectedAssetType === 'berth' ? 'berth' : selectedAssetType === 'resource' ? 'resource' : 'add-on'}`}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  aria-label={
                    selectedAssetType === 'berth'
                      ? 'Select Berth'
                      : selectedAssetType === 'resource'
                        ? 'Select Resource'
                        : 'Select Add-on'
                  }
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: '0.75rem',
                      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                      minHeight: '2.75rem',
                      fontSize: '0.875rem',
                      boxShadow: state.isFocused
                        ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
                        : 'none',
                      '&:hover': { borderColor: '#3b82f6' },
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                      borderRadius: '0.75rem',
                      overflow: 'hidden',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                />
              </div>
            )}

            {/* User Filter */}
            <div>
              <label
                htmlFor="user-search-input"
                className="block text-sm font-medium text-gray-700  contrast:text-gray-500 mb-2"
              >
                Search Users
              </label>
              <div className="relative">
                <input
                  id="user-search-input"
                  type="text"
                  name="userSearch"
                  placeholder="Search by name or email"
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="w-full rounded-xl dark:bg-gray-100 contrast:bg-gray-100 border border-gray-300 dark:border-gray-600 contrast:border-gray-600 px-4 py-2.5 text-sm focus:outline-none focus:ring-3 focus:ring-blue-100 dark:focus:ring-blue-900 contrast:focus:ring-blue-900 focus:border-blue-500 placeholder:text-gray-400 dark:placeholder:text-gray-500 contrast:placeholder:text-gray-500 transition-all duration-200"
                  aria-label="Search users by name or email"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {userFilter && (
                    <button
                      onClick={() => setUserFilter('')}
                      className="text-gray-400 dark:text-gray-500 contrast:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 contrast:hover:text-gray-300 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 contrast:hover:bg-gray-800 transition-colors duration-200"
                      aria-label="Clear user search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {(selectedAssetType ||
            selectedBerth ||
            userFilter ||
            statusFilters.size < totalStatuses) && (
            <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-800 contrast:border-gray-800">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm font-medium text-gray-600 contrast:text-gray-600">
                  Active filters:
                </span>

                {selectedAssetType && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-purple-100 dark:from-purple-900 contrast:from-purple-900 to-indigo-100 dark:to-purple-900 contrast:to-purple-900 text-purple-700 dark:text-purple-200 contrast:text-purple-200 border border-purple-200 dark:border-purple-900 contrast:border-purple-900">
                    {assetTypeConfig[selectedAssetType].label}
                    <button
                      onClick={() => {
                        setSelectedAssetType(null);
                        setSelectedBerth(null);
                      }}
                      className="ml-2 text-purple-500 hover:text-purple-800 dark:text-purple-300 contrast:text-purple-300 transition-colors duration-200"
                      aria-label="Remove asset type filter"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}

                {selectedBerth && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-blue-100 dark:from-blue-900 contrast:from-blue-900 to-cyan-100 dark:to-blue-900 contrast:to-blue-900 text-blue-700 dark:text-blue-200 contrast:text-blue-200 border border-blue-200 dark:border-blue-900 contrast:border-blue-900">
                    {selectedAssetType === 'berth'
                      ? `Berth ${selectedBerth}`
                      : selectedAssetType === 'resource'
                        ? getResourceName(selectedBerth)
                        : getAddonName(selectedBerth)}
                    <button
                      onClick={() => setSelectedBerth(null)}
                      className="ml-2 text-blue-500 hover:text-blue-800 dark:text-blue-300 contrast:text-blue-300 transition-colors duration-200"
                      aria-label="Remove selected asset filter"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}

                {userFilter && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-amber-100 dark:from-amber-950 contrast:from-amber-950 to-yellow-100 dark:to-yellow-950 contrast:to-yellow-950 text-amber-700 dark:text-amber-200 contrast:text-amber-200 border border-amber-200 dark:border-amber-900 contrast:border-amber-900">
                    {userFilter.length > 20
                      ? `${userFilter.substring(0, 20)}...`
                      : userFilter}
                    <button
                      onClick={() => setUserFilter('')}
                      className="ml-2 text-amber-500 dark:text-amber-200 contrast:text-amber-200 hover:text-amber-800 dark:hover:text-amber-300 contrast:hover:text-amber-300 transition-colors duration-200"
                      aria-label="Remove user search filter"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterSection;
